import { useRef, useState } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import ChartVisual from "@/components/ChartVisual";
import { SectionLabel } from "@/components/Reveal";

const STEPS = [
  {
    num: "01",
    title: "Watchzone",
    body: "Before anything else, Atlas marks where price is likely to react. The blue watchzone is your area of interest — no trades yet, just attention.",
    testid: "story-step-watchzone",
  },
  {
    num: "02",
    title: "Blue Confluence",
    body: "Inside the watchzone, the indicator stacks independent factors — structure, liquidity, momentum. When they align, the zone earns your focus.",
    testid: "story-step-confluence",
  },
  {
    num: "03",
    title: "Buy & Sell Areas",
    body: "Confluence crystallises into precise buy and sell areas. You see exactly where the opportunity lives — and where the idea is wrong.",
    testid: "story-step-areas",
  },
  {
    num: "04",
    title: "Confirmation",
    body: "Only when price confirms does the plan activate. Entry, target and invalidation are defined before you commit a single pound.",
    testid: "story-step-confirmation",
  },
];

const ScrollStory = () => {
  const ref = useRef(null);
  const [stage, setStage] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  useMotionValueEvent(scrollYProgress, "change", (p) => {
    setStage(Math.min(3, Math.max(0, Math.floor(p * 4))));
  });

  return (
    <section id="how-it-works" ref={ref} className="relative" style={{ height: "420vh" }} data-testid="how-it-works-section">
      <div className="sticky top-0 min-h-screen flex flex-col justify-center overflow-hidden py-14 sm:py-20">
        <div className="absolute top-0 left-0 right-0 h-px bg-line/60">
          <motion.div className="h-full bg-gold origin-left" style={{ scaleX: scrollYProgress }} data-testid="story-progress-bar" />
        </div>
        <div className="absolute -bottom-40 left-[-10%] w-[560px] h-[560px] rounded-full bg-gold/[0.06] blur-[130px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-4 lg:mb-12">
            <div>
              <SectionLabel>How Atlas Works</SectionLabel>
              <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-50 leading-tight" data-testid="story-title">
                Watch the system <span className="text-gold-gradient">think.</span>
              </h2>
            </div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-slate-500 uppercase hidden sm:block">
              Scroll · {STEPS[stage].num} / 04
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="glass-panel gold-ring chart-frame border border-line p-4 sm:p-6" data-testid="story-chart-panel">
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className="font-mono text-[11px] tracking-[0.25em] text-slate-300">XAUUSD · 1H</span>
                  <span className="font-mono text-[10px] tracking-widest text-gold/80 uppercase">{STEPS[stage].title}</span>
                </div>
                <ChartVisual seed={41} stage={stage} className="w-full h-auto" />
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="space-y-1">
                {STEPS.map((s, i) => {
                  const active = i === stage;
                  const done = i < stage;
                  return (
                    <div
                      key={s.num}
                      data-testid={s.testid}
                      className={`relative pl-7 py-2.5 sm:py-4 transition-opacity duration-500 ${active ? "opacity-100" : done ? "opacity-50" : "opacity-30"}`}
                    >
                      <span
                        className={`absolute left-0 top-5 sm:top-6 w-2.5 h-2.5 rounded-full border transition-colors duration-500 ${
                          active ? "bg-gold border-gold" : done ? "bg-transparent border-gold/60" : "bg-transparent border-slate-600"
                        }`}
                      />
                      {i < STEPS.length - 1 && <span className="absolute left-[4.5px] top-8 bottom-0 w-px bg-line" />}
                      <p className={`font-mono text-[10px] tracking-[0.3em] ${active ? "text-gold" : "text-slate-500"}`}>{s.num}</p>
                      <h3 className={`mt-1 font-display font-bold text-lg sm:text-2xl tracking-tight ${active ? "text-slate-50" : "text-slate-400"}`}>
                        {s.title}
                      </h3>
                      <div className={`${active ? "block" : "hidden"} lg:block`}>
                        <p className={`mt-2 text-sm leading-relaxed max-w-md transition-opacity duration-500 ${active ? "text-slate-400 opacity-100" : "text-slate-500 opacity-60"}`}>
                          {s.body}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ScrollStory;
